import {
  ICardItem,
  ISegmentFly,
} from 'entities/ticket/ui/card-of-ticket/module'
import type { IDirectionDetail } from 'entities/ticket/ui/direction-detail'

import { MySegment, MyTicketItem } from 'shared/api/MyTicketsModel.ts'
import { ITariffStatuses } from 'shared/api/TariffApi'
import { SEARCH_TICKET } from 'shared/constants'
import { showWrongDateFormat } from 'shared/lib/dateFormat.ts'
import { Segment, TicketItem } from 'shared/models/ticket'

import { DATE_FORMAT_LIST } from '../constants/dateFormatList'

export type ResultObject = {
  [key: number]: Segment[] & MySegment[]
}

export interface PrepareDetailTicketInfoResult {
  detailTicketInfo: IDirectionDetail[]
  priceTicket: TicketItem['price']['KGS']
  resultsAllListDirections: Segment[][]
}

const START_NIGHT = 0
const FINISH_NIGHT = 6

const HOURS_IN_MINUTEST = 60

const START_TWO_NUMBERS = 10

const getIsNightTransfer = (time?: string): boolean => {
  if (!time?.length) return false

  const hour = parseInt(time.split(':')[0])

  return hour >= START_NIGHT && hour <= FINISH_NIGHT
}

export const prepareDuration = (data: Segment[] | MySegment[]) => {
  let totalDuration = 0

  data?.forEach((segment) => {
    if (segment?.duration?.flight && segment?.duration?.flight?.common) {
      totalDuration += segment.duration.flight.common || 0
    }

    if (segment?.duration?.transfer && segment?.duration?.transfer?.common) {
      totalDuration += segment.duration.transfer.common || 0
    }
  })

  return {
    hours: Math.floor(totalDuration / HOURS_IN_MINUTEST),
    minutes: totalDuration % HOURS_IN_MINUTEST,
    common: totalDuration,
  }
}

export const prepareTimeShow = (common?: number, addZero?: boolean) => {
  if (!common) {
    return {
      hours: 0,
      minutes: 0,
    }
  }

  const hours = Math.floor(common / HOURS_IN_MINUTEST)
  const minutes = common % HOURS_IN_MINUTEST

  if (addZero) {
    return {
      hours: hours < START_TWO_NUMBERS ? '0' + hours : hours,
      minutes: minutes < START_TWO_NUMBERS ? '0' + minutes : minutes,
    }
  }

  return {
    hours: Math.floor(common / HOURS_IN_MINUTEST),
    minutes: common % HOURS_IN_MINUTEST,
  }
}

export const formatTimeToString = (
  minutes: number | string | undefined,
  defaultHours: string,
  defaultMinutes: string,
): string => {
  const timeData = prepareTimeShow(Number(minutes || 0), true)
  const hours = timeData?.hours ?? defaultHours
  const mins = timeData?.minutes ?? defaultMinutes
  return `${hours}:${mins}`
}

export const prepareShowListTicket = (
  listOptions: TicketItem,
  passengersCount: number,
): ICardItem => {
  const listSegments = listOptions?.segments

  const resultsSegment = listSegments?.reduce((segment, item) => {
    if (!segment[item.direction]) {
      segment[item.direction] = []
    }

    segment[item.direction].push(item)

    return segment
  }, {} as ResultObject)

  const listKeys = Object.keys(resultsSegment)

  const preparedSegments: ISegmentFly[] = listKeys?.map((direction) => {
    const results = resultsSegment[direction as keyof object]

    const providers = results?.map((item) => item.provider)

    const uniqueProviders = Array.from(
      new Map(providers?.map((item) => [item.supplier.code, item])).values(),
    )

    const resultDirectionFly = prepareDuration(results)

    const firstSegment = results.at(0)
    const lastSegment = results.at(-1)

    const hasBaggageRecheck = !!results.find((item) => item.baggage_recheck)

    const hasAirportChange = !!results.find((item) => item.is_airport_change)

    return {
      from: {
        name: firstSegment?.dep?.airport?.code || '',
        time: firstSegment?.dep?.time || '',
        date: firstSegment?.dep?.date,
      },
      to: {
        name: lastSegment?.arr?.airport?.code || '',
        time: lastSegment?.arr?.time || '',
        date: firstSegment?.arr?.date,
      },
      time: {
        time: `${resultDirectionFly?.common}` || '',
        countSegment: `${results?.length}`,
      },
      carrier: uniqueProviders?.map((provider) => ({
        name: provider.supplier.title!,
        image: provider.supplier.logo_url!,
      })),
      baggage_recheck: hasBaggageRecheck,
      is_airport_change: hasAirportChange,
      baggageInfo: {
        baggage: firstSegment?.baggage,
        cbaggage: firstSegment?.cbaggage,
      },
    }
  })

  const countSeats = Math.min(
    ...(listSegments?.map((seats) => seats.seats) || 0),
  )

  return {
    monthly_tranche: listOptions?.monthly_tranche,
    freeSeat: passengersCount + 2 >= countSeats ? countSeats : undefined,
    price: listOptions?.price?.KGS,
    segments: preparedSegments,
    is_baggage: listOptions?.is_baggage.status === ITariffStatuses.AVAILABLE,
    is_cbaggage: listOptions?.is_cbaggage.status === ITariffStatuses.AVAILABLE,
    tag: listOptions.tag,
    is_charter: listOptions?.is_charter,
    is_bonus: listOptions.is_bonus,
    bonus_amount: listOptions.bonus_amount,
    old_price: listOptions?.old_price,
    is_mplus: listOptions.is_mplus,
    mplus_amount: listOptions.mplus_amount,
    baggage: listSegments?.at(0)?.baggage,
    cbaggage: listSegments?.at(0)?.cbaggage,
    favorite_search_key: listOptions?.favorite_search_key,
    is_favorite: listOptions?.is_favorite,
    is_30_minutes_left: listOptions?.is_30_minutes_left,
    is_fare_family: listOptions?.fare_family_flag,
    search_params: listOptions?.search_params,
    mInstallmentInfo: listOptions.minstallment_info,
  }
}

export const prepareShowListMyTicket = (
  listOptions: MyTicketItem,
): ICardItem => {
  const listSegments = listOptions?.flight_details.flight.segments

  const resultsSegment = listSegments?.reduce((segment, item) => {
    if (!segment[item.direction]) {
      segment[item.direction] = []
    }

    segment[item.direction].push(item)

    return segment
  }, {} as ResultObject)

  const listKeys = Object.keys(resultsSegment)

  const preparedSegments: ISegmentFly[] = listKeys?.map((direction) => {
    const results = resultsSegment[direction as keyof object]

    const providers = results?.map((item) => item.provider)

    const uniqueProviders = Array.from(
      new Map(providers?.map((item) => [item.supplier.code, item])).values(),
    )

    const resultDirectionFly = prepareDuration(results)

    const firstSegment = results.at(0)
    const lastSegment = results.at(-1)

    const hasBaggageRecheck = !!results.find((item) => item.baggage_recheck)

    const hasAirportChange = !!results.find((item) => item.is_airport_change)

    return {
      from: {
        name: firstSegment?.dep?.city.title || '',
        time: firstSegment?.dep?.time || '',
        date: firstSegment?.dep?.date,
      },
      to: {
        name: lastSegment?.arr?.city.title || '',
        time: lastSegment?.arr?.time || '',
        date: firstSegment?.arr?.date,
      },
      time: {
        time: `${resultDirectionFly?.common}` || '',
        countSegment: `${results?.length}`,
      },
      carrier: uniqueProviders?.map((provider) => ({
        name: provider.supplier.title!,
        image: provider.supplier.logo_url!,
      })),
      baggage_recheck: hasBaggageRecheck,
      is_airport_change: hasAirportChange,
    }
  })

  return {
    price: String(listOptions?.price),
    segments: preparedSegments,
    is_baggage:
      listOptions?.fare.is_baggage.status === ITariffStatuses.AVAILABLE,
    is_cbaggage:
      listOptions?.fare.is_cbaggage.status === ITariffStatuses.AVAILABLE,
    tag: SEARCH_TICKET.CHEAPEST,
    is_charter: listOptions?.is_charter,
    is_bonus: listOptions.is_bonus,
    bonus_amount: listOptions.bonus_amount,
    is_mplus: listOptions.is_mplus,
    mplus_amount: listOptions.mplus_amount,
    ticket_receipt_url: listOptions.ticket_receipt_url,
    restrictions: listOptions.restrictions,
    favorite_search_key: listOptions.favorite_search_key,
    mInstallmentInfo: listOptions.minstallment_info,
  }
}

export const prepareTicketsItems = (itemsTickets: MyTicketItem) => {
  const flyAllSegments = [
    ...(itemsTickets?.flight_details?.flight?.segments || null),
  ]

  const resultsSegment = flyAllSegments?.reduce((segment, item) => {
    if (!segment[item.direction]) {
      segment[item.direction] = []
    }

    segment[item.direction].push(item)

    return segment
  }, {} as ResultObject)

  const listKeys = Object.keys(resultsSegment)

  const preparedSegments: ISegmentFly[] = listKeys?.map((direction) => {
    const results = resultsSegment[direction as keyof object]

    const providers = results?.map((item) => item.provider)

    const uniqueProviders = Array.from(
      new Map(providers?.map((item) => [item.supplier.code, item])).values(),
    )

    const firstSegment = results.at(0)
    const lastSegment = results.at(-1)

    const resultDirectionFly = prepareDuration(results)

    const hasBaggageRecheck = !!results.find((item) => item.baggage_recheck)

    const hasAirportChange = !!results.find((item) => item.is_airport_change)

    return {
      from: {
        name: firstSegment?.dep?.city.title || '',
        time: firstSegment?.dep?.time || '',
        date: showWrongDateFormat(
          firstSegment?.dep?.date || '',
          DATE_FORMAT_LIST.DAY_MONTH_WEEK_DAY,
        ),
      },
      to: {
        name: lastSegment?.arr?.city.title || '',
        time: firstSegment?.arr?.time || '',
        date: showWrongDateFormat(
          firstSegment?.arr?.date || '',
          DATE_FORMAT_LIST.DAY_MONTH_WEEK_DAY,
        ),
      },
      time: {
        time: `${resultDirectionFly?.common}` || '',
        countSegment: `${results?.length}`,
      },
      carrier: uniqueProviders?.map((provider) => ({
        name: provider.supplier.title!,
        image: provider.supplier.logo_url!,
      })),
      baggage_recheck: hasBaggageRecheck,
      is_airport_change: hasAirportChange,
    }
  })

  return {
    segments: preparedSegments,
    price: String(
      Number(itemsTickets?.price) + Number(itemsTickets.insurance_price || 0),
    ),
    is_baggage: itemsTickets?.is_baggage,
    is_cbaggage: itemsTickets?.is_cbaggage,
    status_ticket: itemsTickets?.status,
    is_charter: itemsTickets?.is_charter,
    is_mplus: itemsTickets.is_mplus,
    mplus_amount: itemsTickets.mplus_amount,
    baggage: flyAllSegments?.at(0)?.baggage,
    cbaggage: flyAllSegments?.at(0)?.cbaggage,
    passengers: itemsTickets.flight_details.tickets[0].passengers.length,
    price_with_promo_code: itemsTickets?.price_with_promo_code,
  }
}

export const prepareDetailTicketInfo = (
  ticket: TicketItem | null,
): PrepareDetailTicketInfoResult | null => {
  if (!ticket) return null

  const { segments, price } = ticket

  const grouped: { [key: number]: Segment[] } = {}

  segments.forEach((item) => {
    if (!grouped[item.direction]) {
      grouped[item.direction] = []
    }
    grouped[item.direction].push(item)
  })

  const resultsAllListDirections = Object.values(grouped)
  const priceTicket = price.KGS

  const detailTicketInfo = resultsAllListDirections?.map((direction) => {
    const firstSegment = direction.at(0)
    const lastSegment = direction.at(direction?.length - 1)

    const resultDirectionFly = prepareDuration(direction)

    return {
      title: `${firstSegment?.dep.city.title} → ${lastSegment?.arr.city.title}`,
      time: resultDirectionFly?.common,

      flyItems: direction?.map((direct) => ({
        airPlane: {
          name: direct?.provider.supplier.title || '',
          timeFly: direct?.duration?.flight?.common || 0,
          icon: direct?.provider.supplier.logo_url || '',
        },
        dep: {
          time: direct?.dep?.time || '',
          date: showWrongDateFormat(
            direct?.dep?.date || '',
            DATE_FORMAT_LIST.FULL_DATE_AVIA,
            ',',
          ),
          city: direct?.dep?.city?.title || '',
          airport: `${direct?.dep?.airport?.title}, ${direct?.dep?.airport?.code}`,
        },
        arr: {
          time: direct?.arr?.time || '',
          date: showWrongDateFormat(
            direct?.arr?.date || '',
            DATE_FORMAT_LIST.FULL_DATE_AVIA,
            ',',
          ),
          city: direct?.arr?.city?.title || '',
          airport: `${direct?.arr?.airport?.title}, ${direct?.arr?.airport?.code}`,
        },
        transfer: direct?.duration?.transfer
          ? {
              name: direct?.arr?.city?.title || '',
              time: direct?.duration?.transfer?.common,
              isNight: getIsNightTransfer(direct?.arr?.time),
              baggage_recheck: direct.baggage_recheck,
              is_airport_change: direct.is_airport_change,
            }
          : undefined,
        baggageInfo: {
          baggage: direct.baggage,
          cbaggage: direct.cbaggage,
        },
      })),
    }
  })

  return { detailTicketInfo, priceTicket, resultsAllListDirections }
}

export const prepareDetailMyTicketInfo = (ticket: MyTicketItem) => {
  const segments = ticket?.flight_details?.flight?.segments

  const grouped: { [key: number]: MySegment[] } = {}

  let isDifficultRoute = false
  let isOneWayRoute = false

  segments.forEach((item) => {
    if (!grouped[item.direction]) {
      grouped[item.direction] = []
    }
    grouped[item.direction].push(item)
  })

  const resultsAllListDirections = Object.values(grouped)

  const priceTicket = ticket?.price

  if (ticket?.flight_details?.flight?.segments_direction?.length === 1) {
    isOneWayRoute = true
  }

  if (ticket?.flight_details?.flight?.segments_direction?.length > 2) {
    isDifficultRoute = true
  } else if (ticket?.flight_details?.flight?.segments_direction?.length === 2) {
    const firstSegment = resultsAllListDirections?.[0]?.[0]
    const lastSegment =
      resultsAllListDirections?.[1]?.[resultsAllListDirections?.[1]?.length - 1]

    if (firstSegment?.dep?.airport?.code !== lastSegment?.arr?.airport?.code) {
      isDifficultRoute = true
    }
  }

  const detailTicketInfo = resultsAllListDirections?.map((direction) => {
    const firstSegment = direction.at(0)
    const lastSegment = direction.at(direction?.length - 1)

    const resultDirectionFly = prepareDuration(direction)

    return {
      time: resultDirectionFly?.common,
      isDifficultRoute: isDifficultRoute,
      title: `${firstSegment?.dep.city.title} - ${lastSegment?.arr.city.title}`,
      flyItems: direction?.map((direct) => ({
        airPlane: {
          name: direct?.provider.supplier.title || '',
          timeFly: direct?.duration?.flight?.common || 0,
          icon: direct?.provider.supplier.logo_url || '',
        },
        dep: {
          time: direct?.dep?.time || '',
          date: showWrongDateFormat(
            direct?.dep?.date || '',
            DATE_FORMAT_LIST.DAY_MONTH_WEEK_DAY_SEPARATE,
            ',',
          ),
          code: direct?.dep?.airport?.code,
          city: direct?.dep?.city?.title || '',
          airport: direct?.dep?.airport?.title || '',
          terminal: direct?.dep?.terminal || '',
        },
        arr: {
          time: direct?.arr?.time || '',
          date: showWrongDateFormat(
            direct?.arr?.date || '',
            DATE_FORMAT_LIST.DAY_MONTH_WEEK_DAY_SEPARATE,
            ',',
          ),
          city: direct?.arr?.city?.title || '',
          code: direct?.arr?.airport?.code,
          airport: direct?.arr?.airport?.title || '',
          terminal: direct?.arr?.terminal || '',
        },
        transfer: direct?.duration?.transfer
          ? {
              name: direct?.arr?.city?.title || '',
              time: direct?.duration?.transfer?.common,
              isNight: getIsNightTransfer(direct?.arr?.time),
              baggage_recheck: direct.baggage_recheck,
              is_airport_change: direct.is_airport_change,
            }
          : undefined,
        flight: `${direct?.carrier?.code}-${direct?.flight_number}`,
        baggageInfo: {
          baggage: firstSegment?.baggage,
          cbaggage: firstSegment?.cbaggage,
        },
      })),
    }
  })

  return {
    priceTicket,
    isOneWayRoute,
    detailTicketInfo,
    resultsAllListDirections,
  }
}

// const prepareBaggageInfo = (baggage?: Baggage) => {
//   if (!baggage) return undefined

//   if (!baggage?.piece || !baggage?.weight) return undefined

//   return `${baggage?.piece} x ${baggage?.weight}`
// }

export function calculateTotalDuration(
  segments: { durationMinutes?: string }[],
): { hours: number; minutes: number } {
  let totalMinutes = 0
  segments.forEach((segment) => {
    if (segment.durationMinutes) {
      totalMinutes += parseInt(segment.durationMinutes)
    }
  })
  const hours = Math.floor(totalMinutes / HOURS_IN_MINUTEST)
  const minutes = totalMinutes % HOURS_IN_MINUTEST
  return { hours, minutes }
}
